import React, { useState, useEffect } from 'react';
import { Input, Button, Radio } from 'components/shared/FormComponent';
import { Table, message, Popconfirm, Empty, TableProps, Tooltip } from 'components/shared/AntComponent';
import { useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { deleteQuestionBank, getQuestionBank } from 'services/store/actions/questionbankAction';
import { EditIcon } from 'assets/icons/CustomIcons';
import { DeleteFilled, SearchOutlined } from 'components/shared/AntIcons';
import 'assets/styles/table.scss';
import ImportModal from 'components/candidate/modal/ImportModal';
import ExcelIcon from 'assets/icons/excel.svg';
import { getSampleFile } from 'services/store/actions/accountAction';
import SampleFileModal from 'components/pages/modal/SampleFileModal';

const QuestionFile: any = "/files/question_sample.xlsx";

interface DataType {
    key: number;
    sno: number;
    Mdquestions_name: string;
    answer: string;
    level_of_difficulty: string;
    created_on: string;
}

const QuestionBank: React.FC = () => {
    const history = useNavigate();
    const dispatch = useDispatch();
    const { questionbankData, loading, success2, error2 } = useSelector((state: any) => state.questionbank);
    const [pageSize, setPageSize] = useState(10);
    const [pageIndex, setPageIndex] = useState(1);
    const [searchValue, setSearchValue] = useState('');
    const data: DataType[] = questionbankData?.data?.data || [];
    const totalRecords = questionbankData?.data?.totalRecords || 0;
    const [showSuccessmsg, setShowSuccessmsg] = useState(false);
    const successmsg = showSuccessmsg ? success2 : false;
    const [showErrMsg, setShowErrMsg] = useState(false);
    const errormsg = showErrMsg ? error2 : false;
    const [openModal, setOpenModal] = useState(false);
    const [openFileModal, setOpenFileModal] = useState(false);
    const [accStatus, setAccStatus] = useState('Active');

    const customLocale = {
        emptyText: <Empty className="p-2" description="No Questions Available" />,
    };

    const handleDownloadLocal = () => {
        const link = document.createElement("a");
        link.href = QuestionFile;
        link.download = "question_sample_file.xlsx";
        link.click();
    };

    const columns: TableProps<DataType>['columns'] = [
        {
            title: 'S.No',
            dataIndex: 'sno',
            key: 'sno',
            render: (id, record, index) => {
                if (pageIndex === 1) {
                    return index + 1;
                } else {
                    return (pageIndex - 1) * pageSize + (index + 1);
                }
            },
        },
        {
            title: 'Questions',
            dataIndex: 'questionText',
            key: 'questionText',
            width: '35%',
        },
        {
            title: 'Course Name',
            dataIndex: 'courseName',
            key: 'courseName',
        },
        {
            title: 'Subject Name',
            dataIndex: 'subjectName',
            key: 'subjectName',
        },
        {
            title: 'Difficulty Level',
            dataIndex: 'levelOfDifficulty',
            key: 'levelOfDifficulty',
        },
        {
            title: 'Answer',
            dataIndex: 'answer',
            key: 'answer',
            width: '10%',
        },
        {
            title: 'Created on',
            dataIndex: 'createdOn',
            key: 'createdOn',
            render: (_: any, record: any) => {
                const originalDate = new Date(record?.createdOn) || null;
                return data ? originalDate?.toLocaleString() : "--";
            },
        },
        {
            title: 'Action',
            dataIndex: 'actions',
            key: 'actions',
            render: (_, record) => {
                return (
                    <div className="d-flex w-100">
                        <div className="pointer my-auto pb-3" onClick={() => editQuestion(record)}>
                            <EditIcon size={34} />
                        </div>
                        <div className="my-auto delete-icon">
                            <Popconfirm
                                title="Delete Question"
                                description="Are you sure to delete this question?"
                                onConfirm={() => deleteQuestion(record)}
                                okText="Yes"
                                cancelText="No"
                            >
                                <DeleteFilled className="text-danger pointer del-icon" />
                            </Popconfirm>
                        </div>
                    </div>
                );
            },
        },
    ];

    const editQuestion = (val: any) => {
        history('/aptitude/add-questionbank', {
            state: {
                title: 'Update Question',
                id: 1,
                questionid: val?.questionId,
            },
        });
    };

    const deleteQuestion = (val: any) => {
        dispatch(deleteQuestionBank(val?.id) as any);
        setShowSuccessmsg(true);
        setShowErrMsg(true);
    };

    useEffect(() => {
        if (successmsg) {
            message.success('Question deleted successfully!');
            setShowSuccessmsg(false);
            setPageIndex(1);
            // ✅ Fixed: correct argument order (page, size, searchVal, status)
            getAnalyticsQuestion(1, pageSize, searchValue, accStatus);
        }
        if (errormsg) {
            if (error2?.data) {
                message.error(error2?.data);
            } else {
                message.error("Question couldn't be deleted");
            }
            setShowErrMsg(false);
        }
    }, [successmsg, errormsg]);

    // Pagination configuration
    const paginationConfig: TableProps<DataType>['pagination'] = {
        position: ['bottomRight'],
        current: pageIndex,
        pageSize: pageSize,
        total: totalRecords,
        onChange: (page: number, newPageSize: number) => {
            setPageIndex(page);
            setPageSize(newPageSize);
            // ✅ Fixed: correct argument order (page, size, searchVal, status)
            getAnalyticsQuestion(page, newPageSize, searchValue, accStatus);
        },
        itemRender: (current: any, type: string, originalElement: any) => {
            if (type === 'prev') {
                return <Button className="prev-btn">Previous</Button>;
            }
            if (type === 'next') {
                return <Button className="next-btn">Next</Button>;
            }
            return originalElement;
        },
    };

    const gotoAddScreen = () => {
        history('/aptitude/add-questionbank', {
            state: {
                title: 'Add Question',
                id: null,
                questionid: 0,
            },
        });
    };

    function getAnalyticsQuestion(page: number, size: number, searchVal: string, status: string) {
        const inputJson = {
            "Input": {
                "IsActive": status?.toLowerCase() === 'active' ? true : false,
                "Course": "",
                "Subject": "",
                "BloomsTaxLevel": "",
                "IsIncludeOption": true,
                "QCategory": "",
                "DifficultyLevel": "",
                "TenantId": Number(localStorage.getItem('tenantID'))
            },
            "DataFinder": {
                "PageSize": size,
                "CurrentPage": page,
                "SortByColumn": "",
                "SortBy": "asc",
                "SearchData": searchVal || ""
            }
        };
        dispatch(getQuestionBank(inputJson) as any);
    }

    useEffect(() => {
        getAnalyticsQuestion(pageIndex, pageSize, searchValue, accStatus);
    }, []);

    const handleSearch = (e: any) => {
        if (e.key === 'Enter') {
            setSearchValue(e.target.value);
            setPageIndex(1);
            // ✅ Fixed: correct argument order (page, size, searchVal, status)
            getAnalyticsQuestion(1, pageSize, e.target.value, accStatus);
        }
    };

    const resetSearch = (e: any) => {
        if (e.key === 'Backspace' && !e.target.value) {
            setSearchValue(e.target.value);
            setPageIndex(1);
            // ✅ Fixed: correct argument order (page, size, searchVal, status)
            getAnalyticsQuestion(1, pageSize, e.target.value, accStatus);
        }
    };

    const openImportModal = () => {
        setOpenModal(true);
    };

    const closeImportModal = () => {
        setOpenModal(false);
    };

    const callBackGrid = () => {
        setPageIndex(1);
        setSearchValue('');
        // ✅ Fixed: correct argument order (page, size, searchVal, status)
        getAnalyticsQuestion(1, pageSize, '', accStatus);
    };

    const handleFileModal = () => {
        const inputJson = {
            "prefix": "question_sample"
        };
        dispatch(getSampleFile(inputJson) as any);
        setOpenFileModal(true);
    };

    const handleBack = () => {
        setOpenFileModal(false);
    };

    const handleStatusChange = (e: any) => {
        setPageIndex(1);
        setSearchValue('');
        setAccStatus(e.target.value);
        // ✅ Fixed: correct argument order (page, size, searchVal, status)
        getAnalyticsQuestion(1, pageSize, '', e?.target?.value);
    };

    return (
        <div className="my-3 children-container">
            <div className="row m-0 page-header">
                <h5 className="col-md-3 text-start text-secondary my-auto fw-bold">Question Bank</h5>
                <div className="col-md-8 pe-1 d-flex ms-auto">
                    <div className="col-md-6 text-start pe-0 ms-auto d-flex">
                        <div className="d-flex me-2">
                            <Radio.Group
                                defaultValue={accStatus}
                                buttonStyle="solid"
                                size="small"
                                className="col pe-0 d-flex"
                                onChange={handleStatusChange}
                            >
                                <Radio.Button className="active" value="Active">
                                    Active
                                </Radio.Button>
                                <Radio.Button className="inactive px-2" value="Inactive">
                                    Inactive
                                </Radio.Button>
                            </Radio.Group>
                        </div>
                        <Input
                            prefix={<SearchOutlined />}
                            value={searchValue}
                            onChange={(e) => setSearchValue(e.target.value)}
                            onKeyDown={(e) => handleSearch(e)}
                            onKeyUp={(e) => resetSearch(e)}
                            placeholder="Search"
                        />
                        <div className="col-auto text-start pe-0 ps-2">
                            <Button type="primary" onClick={openImportModal} className="bg-success h-100">
                                Import
                            </Button>
                        </div>
                        <Button type="primary" onClick={gotoAddScreen} className="bg-primary ms-2">
                            Add
                        </Button>
                        <div className='ms-2 download-file'>
                            <Tooltip title="Download Sample file">
                                <img
                                    src={ExcelIcon}
                                    alt="excel icon"
                                    height={35}
                                    className='pointer'
                                    onClick={handleDownloadLocal}
                                />
                            </Tooltip>
                        </div>
                    </div>
                </div>
            </div>
            <div className="m-2 custom-table">
                <Table
                    columns={columns}
                    scroll={{ x: 'calc(230px + 50%)' }}
                    dataSource={data}
                    rowKey="questionId"
                    loading={loading}
                    pagination={paginationConfig}
                    locale={customLocale}
                />
            </div>
            <ImportModal closeModal={closeImportModal} mid={4} openModal={openModal} callBackGrid={callBackGrid} />
            <SampleFileModal closeModal={handleBack} mid={2} openModal={openFileModal} />
        </div>
    );
};

export default QuestionBank;